import * as React from "react";
import Debug from "debug";
import {
  Box,
  Container,
  Avatar,
  Typography,
  Grid,
  Button,
  makeStyles,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { Formik, Form, Field } from "formik";
import { TextField } from "formik-material-ui";
import {
  ArrowBack as ArrowBackIcon,
  LockOutlined as LockOutlinedIcon,
} from "@material-ui/icons";
import { Link as RouterLink } from "react-router-dom";
import { useProvidedTranslations } from "contexts";
import { useAPI, useJSONSchema } from "hooks";
import Loading from "components/generic/Loading";

const debug = Debug("app:reset-password-new");

const cfg = {
  formSchema: {
    type: "object",
    required: ["email", "password", "repeated-password"],
    additionalProperties: false,
    properties: {
      email: { type: "string", minLength: 1 },
      password: { type: "string", minLength: 1 },
      "repeated-password": { type: "string", minLength: 1 },
    },
  },
  initialValues: {
    email: "",
    password: "",
    "repeated-password": "",
  },
  extraValidation(formData) {
    if (formData.password !== formData["repeated-password"]) {
      return [
        {
          keyword: "must-match-password",
          dataPath: "repeated-password",
          message: "Must match with password",
        },
      ];
    }

    return [];
  },
};

const formInputs = [
  {
    id: "email",
    autoComplete: "email",
    type: "email",
    grid: { xs: 12 },
    component: TextField,
  },
  {
    id: "password",
    autoComplete: "actual-password",
    type: "password",
    grid: { xs: 12, sm: 12 },
    component: TextField,
  },
  {
    id: "repeated-password",
    autoComplete: "new-password",
    type: "password",
    grid: { xs: 12, sm: 12 },
    component: TextField,
  },
];

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "relative",
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(3),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
}));

const GuestResetPasswordNewForm = function ({
  submitForm,
  isSubmitting,
  isFetching,
}) {
  const formatMessage = useProvidedTranslations();
  const classes = useStyles();
  return (
    <Form className={classes.form}>
      <Grid container spacing={2}>
        {formInputs.map((formInput) => (
          <Grid item {...formInput.grid} key={formInput.id}>
            <Field
              component={formInput.component}
              name={formInput.id}
              id={formInput.id}
              type={formInput.type}
              label={formatMessage(
                `app.reset-password-new.form.${formInput.id}.label`
              )}
              fullWidth
              variant="outlined"
              autoComplete={formInput.autoComplete}
            />
          </Grid>
        ))}
      </Grid>
      <Button
        type="submit"
        fullWidth
        disabled={isSubmitting || isFetching}
        variant="contained"
        color="primary"
        className={classes.submit}
      >
        {formatMessage("app.reset-password-new.form.submit")}
      </Button>
    </Form>
  );
};

const GuestResetPasswordNewError = (props) => {
  const { error } = props;
  const formatMessage = useProvidedTranslations();
  const classes = useStyles();

  let errorText;
  switch (error.status) {
    case 404:
      errorText = formatMessage("app.reset-password-new.error.invalid-email");
      break;
    default:
      errorText = error.toString();
      break;
  }
  return (
    <Box width={1} className={classes.subtitle}>
      <Alert severity="error" variant="filled">
        {errorText}
      </Alert>
    </Box>
  );
};

const GuestResetPasswordNew = () => {
  const formatMessage = useProvidedTranslations();
  const classes = useStyles();
  const validate = useJSONSchema(cfg.formSchema, cfg.extraValidation);
  const [isReset, setIsReset] = React.useState(false);
  const [key, setKey] = React.useState();
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    debug("urlParams", urlParams);
    const newKey = urlParams.get("key");
    if (newKey) {
      setKey(newKey);
      // Remove qs
      window.history.replaceState &&
        window.history.replaceState(
          null,
          "",
          window.location.pathname +
            window.location.search
              .replace(/[?&]key=[^&]+/, "")
              .replace(/^&/, "?")
        );
    }
  }, []);
  const handleSuccess = React.useCallback((resp) => {
    // TODO: redirect to /login
    setIsReset(true);
  }, []);
  const [{ isLoading, isFetching, error }, doFetch] = useAPI(
    {
      wait: true,
      endpoint: "login/user/reset-password/new",
      method: "PUT",
    },
    handleSuccess
  );

  const handleSubmit = async ({ email, password }) => {
    doFetch({ body: { email, password, key } });
  };

  let children;
  if (isLoading) {
    children = <Loading />;
  } else if (isReset) {
    children = (
      <Typography component="p" variant="body1">
        {formatMessage("app.reset-password-new.is-reset.body")}
      </Typography>
    );
  } else {
    children = (
      <Formik
        initialValues={cfg.initialValues}
        onSubmit={handleSubmit}
        validate={validate}
        component={GuestResetPasswordNewForm}
        isFetching={isFetching}
      />
    );
  }

  return (
    <Container component="main" maxWidth="sm">
      <div className={classes.paper}>
        <Box position="absolute" left="0">
          <Button component={RouterLink} to="/">
            <Avatar className={classes.avatar}>
              <ArrowBackIcon />
            </Avatar>
          </Button>
        </Box>
        <Avatar className={classes.avatar}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          {formatMessage("app.reset-password-new.title")}
        </Typography>
        {error && <GuestResetPasswordNewError error={error} />}
        {children}
      </div>
    </Container>
  );
};

export default GuestResetPasswordNew;
